import React, { useEffect, useRef, useState } from "react"
import { PencilIcon, CameraIcon, CheckIcon, XMarkIcon } from "@heroicons/react/24/outline"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import Button from "aldoo-ra/Button"
import Tooltip from "aldoo-ra/Tooltip"
import { showSuccessToast } from "aldoo-ra/Toast"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { setLanguageGlobal } from "aldoo-ra/Localization"
import { navigate } from "vike/client/router"
import useT from "../hooks/useT"
import useAsync from "../hooks/useAsync"
import useSiteLanguage from "../hooks/useSiteLanguage"
import { UserProfile, UpdateProfile, UserPreferences } from "../storeApi"
import { useCurrency as useStoreCurrency } from "../store/currency"
import { useStoreCurrency as useStoreConfigCurrency } from "aldoo-ra/Store/currency"
import Input from "aldoo-ra/Input"
import { Section, Field, Card, Loading, ErrorText, ChoiceButton } from "./ui"
import UserAvatar, { avatarPhoto } from "../ui/UserAvatar"
import { UploadUserAvatar } from "aldoo-ra/PlatformAuth/api"


/** Name, avatar, email (read-only), language and logout (user {profile}). */
export default function Profile() {
  const t = useT()
  const logout = PlatformAuth.useLogout()
  const siteLanguage = useSiteLanguage()
  const nameRef = useRef(null)
  const { result, pending, refresh } = useAsync(() => UserProfile("name avatar email language"), [])
  const profile = result && !result.error ? { ...result, avatar: result.avatar || "avatar_1" } : null
  const [name, setName] = useState("")
  const [nameError, setNameError] = useState(null)
  const [editingName, setEditingName] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!profile) return
    setName(profile.name || "")
  }, [profile])

  const save = async (update) => {
    setSaving(true)
    setError(null)
    try {
      const response = await UpdateProfile(update)
      if (response?.error) {
        setError(t(response.error))
        return false
      }
      showSuccessToast(t`Saved`)
      await PlatformAuth.refreshProfile()
      refresh()
      return true
    } catch (e) {
      setError(e.message)
      return false
    } finally {
      setSaving(false)
    }
  }

  // the photo: a click on the avatar picks one from the device, and it is
  // the account's picture everywhere (the navbar too) once uploaded
  const photoInput = useRef(null)
  const [uploading, setUploading] = useState(false)
  const MAX_PHOTO_BYTES = 5 * 1024 * 1024
  const uploadPhoto = async (file) => {
    if (!file) return
    if (!/^image\/(jpeg|png|webp|gif)$/.test(file.type)) return setError(t`Choose a JPG, PNG or WebP picture.`)
    if (file.size > MAX_PHOTO_BYTES) return setError(t`The picture is too large. The limit is 5 MB.`)
    setUploading(true)
    setError(null)
    try {
      const response = await UploadUserAvatar(file, file.type)
      if (!response?.result) return setError(t`The picture could not be uploaded. Try again.`)
      showSuccessToast(t`Saved`)
      await PlatformAuth.refreshProfile()
      refresh()
    } catch {
      setError(t`The picture could not be uploaded. Try again.`)
    } finally {
      setUploading(false)
    }
  }

  const changeUsername = (e) => {
    e.preventDefault()
    if (!name || !name.trim()) {
      setNameError(t`Name is required`)
      nameRef.current?.focus()
      return
    }
    setNameError(null)
    const clean = name.trim()
    if (clean === (profile.name || "")) return setEditingName(false)
    save({ name: clean }).then((ok) => ok && setEditingName(false))
  }

  const openNameEditor = () => {
    setName(profile.name || "")
    setNameError(null)
    setEditingName(true)
    setTimeout(() => nameRef.current?.focus(), 0)
  }

  // The language toggle switches the site language too (old onLanguageChange)
  // the currency: the site's choice (store/currency), kept on the account too
  const { currency, setCurrency, currencies } = useStoreCurrency()
  // with no rates to convert by, the ones the store sells in (its config)
  const { currencies: supported } = useStoreConfigCurrency()
  const currencyCodes = (currencies.length ? currencies.map((entry) => entry.code) : supported).map((code) => String(code).toLowerCase()).sort()
  useEffect(() => {
    // the account's choice follows the reader to this browser
    UserPreferences("currency")
      .then((prefs) => {
        const saved = String(prefs?.currency || "").toLowerCase()
        if (saved && saved !== currency) setCurrency(saved)
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const changeCurrency = (code) => {
    if (!code || code === currency) return
    setCurrency(code)
    UserPreferences({ currency: code }).catch(() => {})
  }

  const changeLanguage = (lang) => {
    if (!lang) return
    save({ language: lang })
    if (lang !== siteLanguage) setLanguageGlobal(lang)
  }

  // asked first: signing out by a stray tap is a nuisance
  const onLogout = () =>
    showPromptDialog({
      title: t`Sign Out`,
      message: t`Are you sure you want to sign out?`,
      confirmText: t`Sign Out`,
      cancelText: t`Cancel`,
      onConfirm: signOut,
    })

  const signOut = async () => {
    await logout()
    navigate("/")
  }

  if (pending && !profile) return <Loading />
  if (!profile) return <ErrorText>{t(result?.error || "Could not load the profile")}</ErrorText>

  const currentLanguage = profile.language || siteLanguage || "en"

  return (
    <Section title={t`Profile`}>
      <ErrorText>{error}</ErrorText>
      <Card className="w-full max-w-[360px] lg:max-w-[600px] flex flex-col gap-6 mx-auto md:mx-0">
        {/* Avatar + name and email */}
        <div className="flex flex-col items-center gap-4">
          {/* the photo of the account (Google gives one, or the user adds it here), else the initials; a click picks a new one */}
          <button
            type="button"
            onClick={() => photoInput.current?.click()}
            disabled={uploading}
            className="group relative h-[130px] w-[130px] rounded-full"
            aria-label={avatarPhoto(profile) ? t`Change photo` : t`Add photo`}
            data-testid="avatar-button"
          >
            <UserAvatar user={{ ...profile, name: name || profile?.name }} size={130} className="bg-paper shadow-sm" data-testid="profile-avatar" />
            {/* across the middle of the circle, where a round crop leaves the most room for the words */}
            <span className="absolute inset-x-0 top-1/2 -translate-y-1/2 bg-theme_black/60 py-1.5 text-center text-[12px] font-semibold text-white opacity-0 transition-opacity group-hover:opacity-100">
              {uploading ? t`Uploading…` : avatarPhoto(profile) ? t`Change photo` : t`Add photo`}
            </span>
            <span className="absolute bottom-1 right-1 flex h-9 w-9 items-center justify-center rounded-full border-2 border-paper bg-primary text-white shadow">
              <CameraIcon className="h-5 w-5" />
            </span>
          </button>

          <input
            ref={photoInput}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={(e) => {
              uploadPhoto(e.target.files?.[0])
              e.target.value = ""
            }}
            data-testid="avatar-file"
          />

          <div className="flex flex-col items-center gap-1">
            {/* the name is the way in: click it to change it */}
            {editingName ? (
              <form 
                className="flex flex-col gap-1" 
                onSubmit={changeUsername} 
                data-testid="name-editor"
              >
                <div className="flex flex-nowrap justify-center gap-2">
                  <Input 
                    variant="aldoo"
                    ref={nameRef} 
                    name="name" 
                    value={name} 
                    onChange={(e) => { setName(e.target.value); setNameError(null) }} 
                    className="w-[200px] md:w-[220px] h-[34px]" 
                  />
                  <div className="flex gap-2">
                    <Tooltip text={t`Apply`}>
                      <Button
                        type="submit"
                        variant="icon"
                        disabled={saving}
                        aria-label={t`Apply`}
                        className="w-8 h-8 hover:bg-neutral px-0"
                      >
                        <CheckIcon className="w-5 h-5 stroke-2" />
                      </Button>
                    </Tooltip>

                    <Tooltip text={t`Cancel`}>
                      <Button
                        type="button"
                        variant="icon"
                        onClick={() => setEditingName(false)} disabled={saving}
                        aria-label={t`Cancel`}
                        className="w-8 h-8 hover:bg-neutral px-0"
                      >
                        <XMarkIcon className="w-5 h-5 stroke-2" />
                      </Button>
                    </Tooltip>
                  </div>
                </div>
                {nameError && <span className="text-[13px] text-error">{nameError}</span>}
              </form>
            ) : (
              <button
                type="button"
                onClick={openNameEditor}
                className="group inline-flex items-center justify-center gap-2 text-[20px] font-bold text-text hover:text-primary transition-colors"
                aria-label={t`Change name`}
                data-testid="name-button"
              >
                {profile.name || t`Aldoo User`}
                <PencilIcon className="w-4 h-4 text-text_secondary group-hover:text-primary" />
              </button>
            )}
            <span className="text-[14px] text-text_secondary">
              {profile.email}
            </span>
          </div>
        </div>
        
        <form className="flex flex-col sm:flex-row items-center justify-between gap-5" onSubmit={(e) => e.preventDefault()}>
          <Field label={t`Currency`}>
            {/* every price on the site, and the cart, in this currency */}
            <div className="flex gap-2" role="radiogroup" data-testid="profile-currency">
              {currencyCodes.map((code) => (
                <ChoiceButton key={code} selected={currency === code} onClick={() => changeCurrency(code)}>
                  {code}
                </ChoiceButton>
              ))}
            </div>
          </Field>

          <Field label={t`Language`}>
            <div className="flex gap-2" role="radiogroup" data-testid="profile-language">
              {["bg", "en"].map((lang) => (
                <ChoiceButton key={lang} selected={currentLanguage === lang} onClick={() => changeLanguage(lang)}>
                  {lang}
                </ChoiceButton>
              ))}
            </div>
          </Field>
        </form>
        <div className="flex justify-end border-t border-bordered pt-4">
          <Button variant="primary" onClick={onLogout} data-testid="logout-button">
            {t`Sign Out`}
          </Button>
        </div>
      </Card>
    </Section>
  )
}
